// https://github.com/paidy/api-consumers-rust/blob/master/src/ops/email_reminder_flow.rs
import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { Platform as ReCaptchaPlatform } from 'apollo/queries/autogenerate/schemas-public';
import EmailReminderInputPhone from 'shared/modules/auth/components/organisms/EmailReminderInputPhone';
import EmailReminderPhoneError from 'shared/modules/auth/components/organisms/EmailReminderPhoneError';
import EmailReminderPhoneSuccess from 'shared/modules/auth/components/organisms/EmailReminderPhoneSuccess';
import EmailReminderPinCode from 'shared/modules/auth/components/organisms/EmailReminderPinCode';
import Dialog from 'shared/modules/common/components/atoms/Dialog';
import generateID from 'shared/modules/common/utils/generate-id';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  reCaptchaPlatform: ReCaptchaPlatform;
  getRecaptchaTokenFunction: () => Promise<string>;
  onNavigateBack: () => void;
};

const EmailReminderUnauthenticatedDialog = (props: Props) => {
  const [stepCounter, setStepCounter] = useState(1);
  const [errorType, setErrorType] = useState<
    'timeout' | 'notice' | 'authError' | null
  >(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpSession, setOTPSession] = useState('');
  const [clearState, setClearState] = useState(false);

  const cleanUpState = () => {
    setStepCounter(1);
    setClearState(true);
  };

  const onClose = () => {
    cleanUpState();
    props.onClose();
  };

  const onNavigateBack = () => {
    cleanUpState();
    props.onNavigateBack();
  };

  const onBackButtonPress = () => {
    if (stepCounter === 1) {
      onNavigateBack();
    } else {
      setStepCounter(stepCounter - 1);
    }
  };

  const onErrorCloseButtonPress = (isTryAgain: boolean) => {
    if (isTryAgain) {
      setStepCounter(1);
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      title="メールアドレスの確認"
      open={props.isVisible}
      showBackButton={stepCounter <= 2}
      onBackButtonPress={onBackButtonPress}
      onCloseButtonPress={onClose}
      style={style.dialog}
    >
      <View
        {...Platform.select({ web: { accessibilityRole: 'form' } })}
        style={style.container}
      >
        {/* STEP 1 - Input phone number */}
        {stepCounter === 1 && (
          <EmailReminderInputPhone
            getRecaptchaTokenFunction={props.getRecaptchaTokenFunction}
            reCaptchaPlatform={props.reCaptchaPlatform}
            onError={() => {
              setStepCounter(4);
              setErrorType('notice');
            }}
            onSuccess={(phone: string, session: string) => {
              setPhoneNumber(phone);
              setOTPSession(session);
              setStepCounter(2);
            }}
            clearState={clearState}
          />
        )}
        {/* STEP 2 - Phone number OTP */}
        {stepCounter === 2 && (
          <View
            style={style.container}
            {...generateID('step2-email-reminder-dialog')}
          >
            <EmailReminderPinCode
              phoneNumber={phoneNumber}
              onError={(type) => {
                setStepCounter(4);
                setErrorType(type);
              }}
              onSuccess={() => {
                setStepCounter(3);
              }}
              getRecaptchaTokenFunction={props.getRecaptchaTokenFunction}
              reCaptchaPlatform={props.reCaptchaPlatform}
              onVoiceCallInitiateSuccess={() => {
                setStepCounter(2);
              }}
              otpSession={otpSession}
            />
          </View>
        )}
        {/* STEP 3 - SUCCESS */}
        {stepCounter === 3 && (
          <EmailReminderPhoneSuccess onCloseButtonPress={onClose} />
        )}
        {stepCounter === 4 && errorType && (
          <EmailReminderPhoneError
            errorType={errorType}
            onErrorCloseButtonPress={onErrorCloseButtonPress}
          />
        )}
      </View>
    </Dialog>
  );
};

const style = StyleSheet.create({
  container: {
    flex: 1,
  },
  dialog: {
    maxHeight: 624,
  },
});

export default EmailReminderUnauthenticatedDialog;
